import { useState } from "react";
import { MessageSquare, CheckSquare, AlignLeft, AtSign, Check, Paperclip } from "lucide-react";
import { Avatar } from "./Avatar";
import { fristInfo, FRIST_KLASSE, medlem, PRI } from "./sakerUtils";

export const TaskCard = ({ sak, members, today, onOpen, onStatus, index = 0 }) => {
  const [dras, setDras] = useState(false);
  const frist = fristInfo(sak.dueDate, today);
  const ansvarlig = medlem(members, sak.assigneeId);
  const delFerdig = (sak.subtasks || []).filter((s) => s.done).length;
  const delTotal = (sak.subtasks || []).length;
  const pri = PRI[sak.priority || 2];
  const kommentarer = (sak.comments || []).length;
  const antVedlegg = (sak.attachments || []).length;
  const harMention = (sak.comments || []).some((k) => (k.mentions || []).length > 0);
  const ferdig = sak.status === "done";
  const harMeta =
    (frist && !ferdig) || delTotal > 0 || kommentarer > 0 || antVedlegg > 0 ||
    (sak.labels || []).length > 0 || ansvarlig || sak.description;

  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/sak", sak.id);
        e.dataTransfer.effectAllowed = "move";
        setDras(true);
      }}
      onDragEnd={() => setDras(false)}
      onClick={() => onOpen(sak.id)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(sak.id); }}
      className={`sak-kort sak-inn w-full text-left bg-white border border-[#E7E4DB] rounded-[10px] px-3.5 py-3 cursor-pointer ${sak.priority === 1 && !ferdig ? "sak-kort-p1" : ""} ${dras ? "dras" : ""}`}
      style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
      data-testid={`saker-kort-${sak.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onStatus(sak.id, ferdig ? "inbox" : "done"); }}
          className={`mt-px w-[17px] h-[17px] rounded-full border-[1.5px] shrink-0 flex items-center justify-center transition-all ${
            ferdig
              ? "bg-emerald-500 border-emerald-500 text-white"
              : "border-neutral-300 text-transparent hover:border-emerald-500 hover:text-emerald-500 hover:scale-110"
          }`}
          title={ferdig ? "Gjenåpne saken" : "Marker som ferdig"}
          data-testid={`saker-kort-check-${sak.id}`}
        >
          <Check size={11} strokeWidth={2.6} />
        </button>
        <p className={`flex-1 text-[13.5px] font-medium leading-snug text-neutral-900 line-clamp-2 ${ferdig ? "line-through text-neutral-400" : ""}`}>
          {sak.title}
        </p>
        {sak.priority !== 2 && (
          <span
            className="bud-mono text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
            style={{ color: pri.farge, background: pri.bg }}
          >
            {pri.kort}
          </span>
        )}
      </div>

      {harMeta && (
        <div className="mt-2.5 pl-[25px] flex items-center gap-2 flex-wrap">
          {frist && !ferdig && (
            <span className={`bud-mono text-[10px] font-semibold px-1.5 py-0.5 rounded ${FRIST_KLASSE[frist.tone]}`}>
              {frist.label}
            </span>
          )}
          {sak.description && <AlignLeft size={11} strokeWidth={2.2} className="text-neutral-300" />}
          {delTotal > 0 && (
            <span className={`inline-flex items-center gap-1 text-[10.5px] font-medium ${delFerdig === delTotal ? "text-emerald-600" : "text-neutral-500"}`}>
              <CheckSquare size={11} strokeWidth={2.2} /> {delFerdig}/{delTotal}
            </span>
          )}
          {kommentarer > 0 && (
            <span className={`inline-flex items-center gap-1 text-[10.5px] font-medium ${harMention ? "text-amber-600" : "text-neutral-500"}`}>
              {harMention ? <AtSign size={11} strokeWidth={2.2} /> : <MessageSquare size={11} strokeWidth={2.2} />} {kommentarer}
            </span>
          )}
          {antVedlegg > 0 && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-neutral-500">
              <Paperclip size={11} strokeWidth={2.2} /> {antVedlegg}
            </span>
          )}
          {(sak.labels || []).slice(0, 2).map((l) => (
            <span key={l} className="text-[10px] font-medium text-neutral-500 border border-[#E7E4DB] rounded-full px-1.5 py-px">
              {l}
            </span>
          ))}
          {(sak.labels || []).length > 2 && (
            <span className="text-[10px] text-neutral-400">+{sak.labels.length - 2}</span>
          )}
          {ansvarlig && <span className="ml-auto"><Avatar person={ansvarlig} size={20} /></span>}
        </div>
      )}

      {delTotal > 0 && !ferdig && (
        <div
          className="sak-fremdrift mt-2.5 ml-[25px]"
          role="progressbar"
          aria-valuenow={delFerdig}
          aria-valuemin={0}
          aria-valuemax={delTotal}
        >
          <div style={{ width: `${Math.round((delFerdig / delTotal) * 100)}%` }} />
        </div>
      )}
    </div>
  );
};
